import {
  DefaultUrlSerializer,
  PRIMARY_OUTLET,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { UserType } from 'shared-general-libs/type/user';
import {
  PORTAL_FOR_USER_TYPE,
  PORTAL_PREFIXES,
} from '@ieCore/consts/portal.consts';
import logger from '@ieShared/utils/logger.utils';

/** The query parameter that carries an attempted URL through the login page. */
export const RETURN_URL_PARAM = 'returnUrl';

/** Anything longer than this is not a real in-app deep link. */
export const RETURN_URL_MAX_LENGTH = 2048;

/**
 * Every whitespace character (`\p{White_Space}` — ASCII space, TAB, NBSP, EM SPACE,
 * the U+2000 block, U+2028/9, IDEOGRAPHIC SPACE…), every control character
 * (`\p{Cc}` — C0, DEL, C1) and every invisible formatting character (`\p{Cf}` —
 * the bidi overrides U+202A-E, SOFT HYPHEN, ZERO WIDTH JOINER, BOM…).
 *
 * Unicode property escapes rather than hand-written ranges: the ranges kept
 * missing characters (EM SPACE and RIGHT-TO-LEFT OVERRIDE both slipped through an
 * earlier revision), and matching "all of this Unicode category" is exactly what
 * the categories are for. Also keeps every literal control character out of this
 * source file.
 */
const INVISIBLE_CHARACTER = /[\p{White_Space}\p{Cc}\p{Cf}]/u;

/**
 * The Angular parser the Router itself uses (`Router.parseUrl()` delegates to the
 * injected `UrlSerializer`, which is a `DefaultUrlSerializer` by default). Held
 * as a module constant so `safeReturnUrl()` stays a pure function: no injector,
 * no `TestBed`, directly spec-able.
 */
const urlSerializer = new DefaultUrlSerializer();

function reject(reason: string): null {
  // Debug-only, and deliberately without the value: a rejected returnUrl is
  // attacker-controlled input and is never echoed anywhere.
  logger.log(`returnUrl rejected: ${reason}`);

  return null;
}

/**
 * `allowPlainSpace` is the one deliberate carve-out: after a single decode an
 * ASCII space is legitimate, because it came from `%20` inside a query value.
 * Every other whitespace, control or formatting character stays forbidden in both
 * passes — an EM SPACE or a RIGHT-TO-LEFT OVERRIDE in a URL is never benign, and a
 * raw (un-decoded) space means the input was never a real in-app URL.
 */
function hasInvisibleCharacter(
  value: string,
  allowPlainSpace: boolean,
): boolean {
  for (const character of value) {
    if (character === ' ') {
      if (!allowPlainSpace) {
        return true;
      }

      continue;
    }

    if (INVISIBLE_CHARACTER.test(character)) {
      return true;
    }
  }

  return false;
}

/** Steps 2-4 of the validator, run over both the raw and the decoded string. */
function hasSafeShape(value: string, allowPlainSpace: boolean): boolean {
  // [2] must be a site-root-relative path — kills `https://`, `javascript:`,
  //     `data:`, `http:/evil.com` and every other scheme or bare-relative form.
  if (!value.startsWith('/')) {
    return false;
  }

  // [3] protocol-relative open redirect: the browser treats `//host` and `/\host`
  //     as absolute even though they look relative.
  if (value.startsWith('//') || value.startsWith('/\\')) {
    return false;
  }

  // [4] no backslash anywhere, and nothing invisible.
  return (
    !value.includes('\\') && !hasInvisibleCharacter(value, allowPlainSpace)
  );
}

/**
 * The string-level half of the traversal check. **Load-bearing — do not delete.**
 *
 * This and `hasSafeTree()` are **complementary, not primary-and-backup**: each
 * catches a class the other misses, so BOTH are required.
 *
 * - Only the parsed check catches an encoded `?`. `/enterprise/%3F/%2e%2e/%2e%2e/admin`
 *   decodes to `/enterprise/?/../../admin`, so splitting on `?` here discards both
 *   `..` segments — while Angular parses them as real path segments. Matrix
 *   parameters (`..;a=b`) hide from this check for the same reason.
 * - **Only this check catches an encoded `/`.** `/enterprise/%2e%2e%2f%2e%2e/admin`
 *   parses to a SINGLE segment whose `path` is the literal string `"../.."`, so
 *   `segment.path === '..'` is false and `hasSafeTree()` returns true. Decoding the
 *   whole string first, as this does, turns it back into `/enterprise/../../admin`
 *   and the split finds both segments.
 *
 * Every one of those forms has its own spec case. Deleting either check turns some
 * of them green-to-red — that is the intended tripwire.
 */
function hasDotSegment(value: string): boolean {
  const path = value.split(/[?#]/)[0];

  return path.split('/').some((segment) => segment === '.' || segment === '..');
}

/**
 * The parsed-tree half of the check, and the sole owner of the outlet rule.
 * Walks every segment group, not just the primary top level:
 *
 * - **any non-primary outlet is rejected** — this check owns that rule outright.
 *   Only the first primary segment gets a portal check, but step 8 re-serialises
 *   the WHOLE tree, so an auxiliary outlet such as `/enterprise(aux:admin)` would
 *   be handed back entirely unvalidated. No named outlet in this app is a
 *   legitimate returnUrl target, so the asymmetry is closed by refusing them
 *   outright rather than by validating each one.
 * - **any `.` or `..` segment at any depth is rejected** — here the parser has
 *   already decoded each segment and split its matrix parameters off (`..;a=b`
 *   arrives as `path: '..'`), catching what the string check cannot. It does NOT
 *   supersede `hasDotSegment()`: an encoded `/` keeps a traversal inside one
 *   segment (`path === '../..'`) and slips past this check. See that function.
 */
function hasSafeTree(group: UrlSegmentGroup): boolean {
  for (const outlet of Object.keys(group.children)) {
    if (outlet !== PRIMARY_OUTLET) {
      return false;
    }
  }

  for (const segment of group.segments) {
    if (segment.path === '.' || segment.path === '..') {
      return false;
    }
  }

  return Object.values(group.children).every(hasSafeTree);
}

function decodeOnce(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed percent-encoding: not a URL we are willing to navigate to.
    return null;
  }
}

/**
 * The single choke point for the returnUrl chain (design §2.3[4]).
 *
 * Returns a safe, internal, role-appropriate URL to navigate to, or `null` when
 * the caller must fall back to the user's default home. This is an open-redirect
 * surface: every caller MUST route through this function and MUST treat `null`
 * as "use the existing behaviour", never as "navigate to the raw input".
 *
 * Fine-grained permissions for the destination are deliberately NOT checked here
 * — the destination route's own guards own those, and duplicating them would
 * drift. The portal prefix + user type check is exactly what is needed to avoid
 * dumping a mismatched user on `/not-found` (DP-10).
 */
export function safeReturnUrl(
  raw: string | null | undefined,
  userType: UserType,
): string | null {
  // [1] present, non-empty, not absurdly long. An ABSENT returnUrl is the normal
  //     two-argument login, not a rejection — say nothing about it.
  if (typeof raw !== 'string' || raw.length === 0) {
    return null;
  }

  if (raw.length > RETURN_URL_MAX_LENGTH) {
    return reject('over the length cap');
  }

  // [2][3][4] on the raw string.
  if (!hasSafeShape(raw, false)) {
    return reject('unsafe shape');
  }

  // ...and again after a single decode, so an encoded form cannot re-introduce
  // any of the above.
  const decoded = decodeOnce(raw);
  if (decoded === null) {
    return reject('malformed encoding');
  }

  if (!hasSafeShape(decoded, true)) {
    return reject('unsafe shape once decoded');
  }

  if (hasDotSegment(raw) || hasDotSegment(decoded)) {
    return reject('path traversal');
  }

  // [5] parse with Angular's own parser rather than doing string surgery.
  let tree: UrlTree;
  try {
    tree = urlSerializer.parse(raw);
  } catch {
    return reject('unparseable');
  }

  // [5b] the authoritative traversal + outlet check, over the parsed tree.
  //
  // `DefaultUrlSerializer.parse()` does NOT resolve dot segments — it round-trips
  // `..` verbatim as an ordinary path segment (Angular resolves dot segments in
  // `createUrlTree()`, for RELATIVE navigation only). So the design's claim that
  // parsing "normalises `/enterprise/../../admin/x` before the prefix check" is
  // wrong, and without this check that URL would pass step 6 on its literal first
  // segment, `enterprise`.
  //
  // Rejecting it outright — rather than resolving it ourselves and re-checking —
  // also stops the BROWSER from doing the normalising: on `navigateByUrl()` the
  // location update writes `/admin/companies` into the address bar while
  // `NotFoundComponent` renders, which is exactly the "your link is broken"
  // confusion DP-10 exists to prevent.
  if (!hasSafeTree(tree.root)) {
    return reject('traversal or non-primary outlet');
  }

  const segments = tree.root.children[PRIMARY_OUTLET]?.segments ?? [];
  if (segments.length === 0) {
    return reject('no path segment');
  }

  const prefix = segments[0].path;

  // [6] a known portal, and nothing else.
  if (!(PORTAL_PREFIXES as readonly string[]).includes(prefix)) {
    return reject('not a portal prefix');
  }

  // [7] and the portal this user actually belongs to.
  if (PORTAL_FOR_USER_TYPE[userType] !== prefix) {
    return reject('portal does not match the user type');
  }

  // [8] hand back what the parser produced, never the raw string.
  return urlSerializer.serialize(tree);
}
